import React, {useContext, useEffect, useState} from 'react';
import Table from '@material-ui/core/Table';
import {NxLoader} from '@nextbank/ui-components';
import useGet from '../../../../../../../shared/hooks/use-get.hook';
import TableHeader from '../../../../../../shared/table/header/TableHeaders';
import WaiveTableBody from './WaiveTableBody';
import {getWaiveTableHeaders} from '../../../../../../../utils/table-helper';
import {CBSFee} from '../../../../../../../shared/model/cbs-fee';
import {WaiveInput, WaiveType} from '../../../../../../../shared/model/consolidate-loans';
import {CBSLoanDetails} from '../../../../../../../shared/model/cbs-loans';
import {ApiHelper} from '../../../../../../../utils/api-helper';
import {AppSnackbarContext} from '../../../../../../shared/app-snackbar-provider/AppSnackbarProvider';
import {WaiveInputContext} from './waive-input-context';
import {getWaivableCustomFees} from './waivable-custom-fees';

interface Props {
  cbsLoanDetails: CBSLoanDetails;
  loading: boolean,
  setLoading: (value: boolean) => void;
}

const WaiveTable = ({cbsLoanDetails, loading, setLoading}: Props): React.ReactElement => {

  const HEADERS_TRANS_PREFIX = 'LOAN_CONFIGURATIONS.LOAN_APPLICATION_DATA.CONSOLIDATED_LOANS';
  const columnHeaders = getWaiveTableHeaders();
  const {addWaiveInputs} = useContext(WaiveInputContext);
  const {showErrorMessage} = useContext(AppSnackbarContext);

  const getCbsLoanFees = useGet<CBSFee[]>(ApiHelper.getCbsLoanFeesUrl(cbsLoanDetails.loanId));
  const getCbsLoansPreterminationFees = useGet<CBSFee[]>(ApiHelper.getCbsLoanTerminationFeesUrl(cbsLoanDetails.loanId));
  const getCbsLoansPreterminationEarnedInterest = useGet<number>(ApiHelper.getCbsLoanPreterminationEarnedInterest(
    cbsLoanDetails.loanId));

  const [customFees, setCustomFees] = useState<CBSFee[]>([]);

  const handleAddWaiveInputs = (customFees: CBSFee[], asEarnedInterest?: number): void => {
    const waiveInputs: WaiveInput[] = [{
      waiveAmount: asEarnedInterest ? asEarnedInterest : 0,
      waiveType: WaiveType.INTEREST,
      loanId: cbsLoanDetails.loanId,
      feeDefinitionId: undefined
    },
      {
        waiveAmount: 0,
        waiveType: WaiveType.PAST_DUE_INTEREST,
        loanId: cbsLoanDetails.loanId,
        feeDefinitionId: undefined
      },
      {
        waiveAmount: 0,
        waiveType: WaiveType.PENALTY,
        loanId: cbsLoanDetails.loanId,
        feeDefinitionId: undefined
      },
      {
        waiveAmount: 0,
        waiveType: WaiveType.CBU,
        loanId: cbsLoanDetails.loanId,
        feeDefinitionId: undefined
      },
      {
        waiveAmount: 0,
        waiveType: WaiveType.TP,
        loanId: cbsLoanDetails.loanId,
        feeDefinitionId: undefined
      },
      {
        waiveAmount: 0,
        waiveType: WaiveType.PF,
        loanId: cbsLoanDetails.loanId,
        feeDefinitionId: undefined
      }
    ];

    const waiveInputCustomFees: WaiveInput[] = customFees.map(fee => {
      return {
        waiveAmount: 0,
        waiveType: WaiveType.CUSTOM_FEE,
        loanId: cbsLoanDetails.loanId,
        feeDefinitionId: fee.feeDefinitionId
      };
    });
    addWaiveInputs(cbsLoanDetails.loanId, [...waiveInputs, ...waiveInputCustomFees]);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([getCbsLoanFees(), getCbsLoansPreterminationFees()])
      .then(([loanFees, preterminationFees]) => {
        const customFees = getWaivableCustomFees(loanFees, preterminationFees);
        setCustomFees(customFees);
        return customFees;
      })
      .then((customFees) => {
        if (cbsLoanDetails.asEarnedPreterminationInterestCalculation) {
          getCbsLoansPreterminationEarnedInterest()
            .then(asEarnedInteres => handleAddWaiveInputs(customFees, asEarnedInteres));
        } else {
          handleAddWaiveInputs(customFees);
        }
      })
      .catch(error => showErrorMessage(error));
  }, []);

  return <>
    {loading ? (<NxLoader />) : (
      <Table>
        <TableHeader translationPath={HEADERS_TRANS_PREFIX} headers={columnHeaders} />
        <WaiveTableBody cbsLoanDetails={cbsLoanDetails} customFees={customFees} />
      </Table>
    )}
  </>;
};

export default WaiveTable;
